import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  ClockIcon,
  CalendarIcon,
  DocumentTextIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  CreditCardIcon,
  XMarkIcon,
  BellIcon,
  SpeakerWaveIcon,
  ClipboardDocumentCheckIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import PortalSwitcher from '../ui/PortalSwitcher';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, isAdmin, isHR, isManager } = useAuth();
  const location = useLocation();

  const navigationItems = [
    {
      name: 'Dashboard',
      href: isAdmin || isHR || isManager ? '/management-dashboard' : '/dashboard',
      icon: HomeIcon,
      roles: ['admin', 'hr', 'manager', 'team_lead', 'employee']
    },
    {
      name: 'Attendance',
      href: '/attendance',
      icon: ClockIcon,
      roles: ['admin', 'hr', 'manager', 'team_lead', 'employee']
    },
    {
      name: 'Calendar',
      href: '/calendar',
      icon: CalendarIcon,
      roles: ['admin', 'hr', 'manager', 'team_lead', 'employee']
    },
    {
      name: 'Leave Request',
      href: '/leave-request',
      icon: DocumentTextIcon,
      roles: ['admin', 'hr', 'manager', 'team_lead', 'employee']
    },
    {
      name: 'WFH Request',
      href: '/wfh-request',
      icon: HomeIcon,
      roles: ['admin', 'hr']
    },
    {
      name: 'Leave Approval',
      href: '/leave-approval',
      icon: DocumentTextIcon,
      roles: ['admin', 'hr']
    },
    {
      name: 'Attendance Correction',
      href: '/attendance-correction',
      icon: ClockIcon,
      roles: ['admin', 'hr', 'manager', 'team_lead', 'employee']
    },
    {
      name: 'Mark Attendance',
      href: '/mark-attendance',
      icon: ClipboardDocumentCheckIcon,
      roles: ['admin', 'hr']
    },
    {
      name: 'Employees',
      href: '/employees',
      icon: UsersIcon,
      roles: ['admin', 'hr']
    },
    // {
    //   name: 'Teams',
    //   href: '/teams',
    //   icon: BuildingOfficeIcon,
    //   roles: ['admin', 'hr']
    // },
    {
      name: 'Reports',
      href: '/reports',
      icon: ChartBarIcon,
      roles: ['admin', 'hr']
    },
    {
      name: 'Claims',
      href: '/claims',
      icon: CreditCardIcon,
      roles: ['admin', 'hr', 'manager', 'team_lead', 'employee', 'accountant']
    },
    {
      name: 'Notifications',
      href: '/notifications',
      icon: BellIcon,
      roles: ['admin', 'hr', 'manager', 'team_lead', 'employee']
    },
    {
      name: 'Announcements',
      href: '/announcements',
      icon: SpeakerWaveIcon,
      roles: ['admin', 'hr', 'manager', 'team_lead', 'employee']
    },
    {
      name: 'WFH Management',
      href: '/wfh-management',
      icon: BriefcaseIcon,
      roles: ['admin', 'hr']
    },
    {
      name: 'Leave Management',
      href: '/leave-management',
      icon: DocumentTextIcon,
      roles: ['admin', 'hr']
    }
  ];

  const filteredNavigation = navigationItems.filter(item => 
    item.roles.includes(user?.role?.toLowerCase())
  );



  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 40
      }
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 40
      }
    }
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex w-64 flex-col">
          <div className="flex flex-grow flex-col overflow-x-hidden overflow-y-auto border-r border-slate-200 bg-sky-50 pt-4 pb-3">
            {/* Portal Switcher */}
            <div className="flex items-center flex-shrink-0 px-4">
              <div className="w-full">
                <PortalSwitcher currentPortal="hrms" />
              </div>
            </div>

            {/* Navigation */}
            <nav className="mt-2 flex-1 space-y-1 px-2">
              {filteredNavigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center rounded-lg border-l-2 border-transparent px-3.5 py-2.5 text-base font-medium transition-colors duration-150 ${
                      isActive
                        ? 'bg-primary-50 text-primary-600 !border-primary-600'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <item.icon
                      className={`mr-3 h-5 w-5 flex-shrink-0 ${
                        isActive ? 'text-primary-600' : 'text-slate-500 group-hover:text-slate-700'
                      }`}
                    />
                    {item.name}
                  </NavLink>
                );
              })}
            </nav>

          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <motion.div
        className="fixed inset-y-0 left-0 z-50 w-64 bg-sky-50 shadow-xl lg:hidden"
        variants={sidebarVariants}
        initial="closed"
        animate={isOpen ? "open" : "closed"}
      >
        <div className="flex flex-col h-full overflow-x-hidden">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 p-3">
            <div className="mr-3 flex-1">
              <PortalSwitcher currentPortal="hrms" />
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-1.5 text-gray-400 hover:bg-white hover:text-primary-600 flex-shrink-0"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={`group flex items-center rounded-lg border-l-2 border-transparent px-3.5 py-2.5 text-base font-medium transition-colors duration-150 ${
                    isActive
                      ? 'bg-primary-50 text-primary-600 !border-primary-600'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <item.icon
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive ? 'text-primary-600' : 'text-slate-500 group-hover:text-slate-700'
                    }`}
                  />
                  {item.name}
                </NavLink>
              );
            })}
          </nav>

        </div>
      </motion.div>
    </>
  );
};

export default Sidebar;
